

import { createSlice } from '@reduxjs/toolkit';

const initialState = {
    data: [],
    indexTracker: {}
}

const devicesSlice = createSlice({
    name: 'devices',
    initialState,
    reducers: {
        setDevices: (state, action) => {
            const { deviceID, twin } = action.payload;
            const existing = state.data.find(d => d.deviceID === deviceID);
            if (existing) {
                existing.twin = twin;
            } else {
                state.data.push({ deviceID, meta: {}, twin });
            }
        },

        setAllMeta: (state, action) => {
            const metaMap = action.payload;
            for (const deviceID in metaMap) {
                const device = state.data.find(d => d.deviceID === deviceID);
                if (device) {
                    device.meta = metaMap[deviceID];
                } else {
                    state.data.push({ deviceID, meta: metaMap[deviceID], twin: {} });
                }
            }
        },

        updateMeta: (state, action) => {
            const { deviceID, field, value } = action.payload;
            const device = state.data.find(d => d.deviceID === deviceID);
            if (device) {
                device.meta = { ...(device.meta || {}), [field]: value };
            }
        },

        // "Delete" a device from local state.  Used after a successful
        // delete-device call so the card disappears immediately rather
        // than waiting for the next heartbeat.  The device will reappear
        // automatically when it next reports in (via updateDevices).
        //
        // Important: we only clear the twin, not the whole row. The
        // device-list filter in Devices/index.js already hides any device
        // with an empty twin, so this is enough to make the card vanish —
        // but it keeps `meta` (name/type) intact in local state. If we
        // dropped the row entirely, the next heartbeat would re-add the
        // device via updateDevices with a blank meta object, silently
        // losing its name/type until the user re-entered them.
        removeDevice: (state, action) => {
            const { deviceID } = action.payload;
            const device = state.data.find(d => d.deviceID === deviceID);
            if (device) {
                device.twin = {};
            }
        },

        updateDevices: (state, action) => {
            const { timestamp, values } = action.payload;
            if (!values) return;

            const stamped = {};
            for (const deviceID in values) {
                stamped[deviceID] = {};
                const deviceUpdate = values[deviceID] || {};
                for (const type in deviceUpdate) {
                    if (type !== 'status' && type !== 'config') continue;
                    stamped[deviceID][type] = {};
                    const typeUpdate = deviceUpdate[type] || {};
                    for (const key in typeUpdate) {
                        stamped[deviceID][type][key] = { value: typeUpdate[key], timestamp };
                    }
                }
            }

            // Update existing devices
            state.data = state.data.map(device => {
                const incoming = stamped[device.deviceID];
                if (!incoming) return device;

                const nextTwin = { ...device.twin };
                if (incoming.status) nextTwin.status = { ...(device.twin?.status || {}), ...incoming.status };
                if (incoming.config) nextTwin.config = { ...(device.twin?.config || {}), ...incoming.config };

                return { ...device, twin: nextTwin };
            });

            // Add any new devices not present in the initial load
            const knownIDs = new Set(state.data.map(d => d.deviceID));
            for (const deviceID in stamped) {
                if (!knownIDs.has(deviceID)) {
                    state.data.push({ deviceID, meta: {}, twin: stamped[deviceID] });
                }
            }
        }
    },
});

export const { setDevices, updateDevices, setAllMeta, updateMeta, removeDevice } = devicesSlice.actions;
export default devicesSlice.reducer;